import React, { createContext, useState } from "react";
import Axios from "../utils/Axios";
import { useTranslation } from "react-i18next";

interface GeoInterface {
    languages: SelectOption[];
    getLanguages: (forFilter?: boolean) => Promise<SelectOption[]>;
    languagesLoading: boolean;
    countries: SelectOption[];
    getCountries: () => void;
    countriesLoading: boolean;
    salesOrganizations: SelectOption[];
    getSalesOrgsForCountry: (code: string) => void;
    getSalesOrgs: (forFilter?: boolean) => void;
    salesOrganizationsLoading: boolean;

}

interface SelectOption {
    label: string;
    value: string;
}

export const GeoContext = createContext<GeoInterface>({
    languages: [],
    getLanguages: () => {
        throw new Error('getLanguages() method not implemented');
    },
    languagesLoading: false,
    countries: [],
    getCountries: () => {
        throw new Error('getCountries() method not implemented');
    },
    countriesLoading: false,
    salesOrganizations: [],
    getSalesOrgsForCountry: () => {
        throw new Error('getSalesOrgsForCountry() method not implemented');
    },
    getSalesOrgs: () => {
        throw new Error('getSalesOrgs() method not implemented');
    },
    salesOrganizationsLoading: false
});

export const GeoProvider: React.FC = ({ children }) => {

    const [languages, setLanguages] = useState([]);
    const [languagesLoading, setLanguagesLoading] = useState(false);
    const [countries, setCountries] = useState([]);
    const [countriesLoading, setCountriesLoading] = useState(false);
    const [salesOrganizations, setSalesOrganizations] = useState([]);
    const [salesOrganizationsLoading, setSalesOrganizationsLoading] = useState(false);
    const { t } = useTranslation();

    function upperCaseFirst(s: string) {
        return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
    }

    const getLanguages = (forFilter?: boolean) => {
        setLanguagesLoading(true);
        const accessToken = localStorage.getItem('access_token');
        return Axios.get('/languages', { headers: { 'Authorization': `Bearer ${accessToken}` } }).then((res) => {
            const langArray = res.data.map((element: any) => {
                const desc = t(element.zlanguage_desc.toString().toUpperCase());
                return { label: upperCaseFirst(desc), value: element.zlanguage_code } as SelectOption;
            });
            langArray.sort((first: SelectOption, second: SelectOption) => first.label.localeCompare(second.label));
            if (forFilter) langArray.unshift({ label: t("ALL_LANGUAGES"), value: 'all' } as SelectOption);
            setLanguages(langArray);
            setLanguagesLoading(false);
            return langArray;
        }).catch((error) => {
            console.error(error);
            setLanguagesLoading(false);
            return [];
        });
    }

    const getCountries = () => {
        setCountriesLoading(true);
        Axios.get('/countries').then((res) => {
            const countriesArray = res.data.map((element: any) => {
                return { label: t(element.zcountry_desc), value: element.zcountry_code } as SelectOption;
            });
            countriesArray.sort((first: SelectOption, second: SelectOption) => first.label.localeCompare(second.label));
            setCountries(countriesArray);
            setCountriesLoading(false);
        }).catch((error) => {
            console.error(error);
            setCountriesLoading(false);
        });
    }

    const getSalesOrgsForCountry = (countryCode: string) => {
        setSalesOrganizationsLoading(true);
        const accessToken = localStorage.getItem('access_token');
        Axios.get(`/salesorgs?zsalesorg_country_code=${countryCode}`, { headers: { 'Authorization': `Bearer ${accessToken}` } }).then((res) => {
            const getSalesOrgsForCountry = res.data.map((element: any) => {
                return { label: element.zsalesorg_name, value: element.zsalesorg_code } as SelectOption;
            });
            setSalesOrganizations(getSalesOrgsForCountry);
            setSalesOrganizationsLoading(false);
        }).catch((error) => {
            console.error(error);
            setSalesOrganizationsLoading(false);
        });
    }

    const getSalesOrgs = (forFilter?: boolean) => {
        setSalesOrganizationsLoading(true);
        const accessToken = localStorage.getItem('access_token');
        Axios.get('/salesorgs', { headers: { 'Authorization': `Bearer ${accessToken}` } }).then((res) => {
            const salesOrgs = res.data.map((element: any) => {
                return { label: element.zsalesorg_name, value: element.zsalesorg_code } as SelectOption;
            });
            salesOrgs.sort((first: SelectOption, second: SelectOption) => first.label.localeCompare(second.label));
            if (forFilter) salesOrgs.unshift({ label: t("ALL_SALES_ORGS"), value: 'all' } as SelectOption);
            setSalesOrganizations(salesOrgs);
            setSalesOrganizationsLoading(false);
        }).catch((error) => {
            console.error(error);
            setSalesOrganizationsLoading(false);
        });
    }


    return <GeoContext.Provider value={{
        languages,
        getLanguages,
        languagesLoading,
        countries,
        getCountries,
        countriesLoading,
        salesOrganizations,
        getSalesOrgsForCountry,
        salesOrganizationsLoading,
        getSalesOrgs
    }}>{children}</GeoContext.Provider>;
}



